import { useMe } from "@/api/services/main/auth";
import { IconBackArrow, IconChevronDown, IconForwardArrow, IconLogo, IconNotification } from "@/assets/icons";
import { EllipsisText } from "@/components/elements/EllipsisText";
import { Linkable } from "@/components/elements/Linkable";
import { Modal } from "@/components/elements/Modal";
import { Popover } from "@/components/elements/Popover";
import { HelpHeaderTooltip } from "@/components/layout/elements/AppHeader/HelpHeaderTooltip";
import { useTokenClaims } from "@/features/auth/hooks/useTokenClaims";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { AppRoutes } from "@/routes/config";
import { background, divider, text } from "@/theme/colors";
import { elevation, spacing } from "@/theme/variables";
import { Box, Button, Stack, styled, Tooltip, Typography } from "@mui/material";
import zIndex from "@mui/material/styles/zIndex";
import { FC, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

type TAppHeaderProps = {
  isOpenDrawer: boolean;
  toggleDrawer: () => void;
};

export const AppHeader: FC<TAppHeaderProps> = ({ isOpenDrawer, toggleDrawer }) => {
  const { isLaptop } = useMediaQuery();
  const navigate = useNavigate();
  const { data: meResult } = useMe();
  const meData = meResult?.data;
  const { isImpersonating } = useTokenClaims();
  const [isOpenLogoutConfirm, setIsOpenLogoutConfirm] = useState(false);

  const handleClickLogout = () => {
    if (isImpersonating) {
      navigate(AppRoutes.logout);
      return;
    }
    setIsOpenLogoutConfirm(true);
  };

  return (
    <BoxWrapper>
      <Stack direction="row" alignItems="center" gap={1}>
        {isLaptop && (
          <Tooltip title={isOpenDrawer ? "メニューを最小化" : "メニューを最大化"} placement="bottom-start">
            <Button onClick={toggleDrawer}>
              {isOpenDrawer ? <IconBackArrow width={32} height={32} /> : <IconForwardArrow width={32} height={32} />}
            </Button>
          </Tooltip>
        )}
        <StyledLink to={AppRoutes.home}>
          <Stack flexDirection="row" alignItems="flex-end">
            <IconLogo />
            <Typography variant="sub16Bold" color={text.white} sx={{ marginLeft: "8px" }}>
              center
            </Typography>
          </Stack>
        </StyledLink>
      </Stack>
      <AppMenu mt={1}>
        <Box mr={2} mb={0.5}>
          <HelpHeaderTooltip />
        </Box>
        <Linkable href="">
          <IconNotification cursor="pointer" fontSize={24} hasDot={false} />
        </Linkable>
        <EllipsisText
          title={meData ? `${meData.fullName}` : "---- | ----"}
          maxWidth={200}
          sx={{ color: text.white, mr: "13px", ml: "24px", mb: 1, fontSize: 14, fontWeight: 500 }}
        />
        <Popover
          sx={{
            marginTop: "8px",
            borderRadius: 0,
            ".MuiPopover-paper": {
              borderRadius: "0 0 8px 8px",
              boxShadow: "0px 4px 4px 0px #00000040",
            },
          }}
          anchor={<IconChevronDown cursor="pointer" fontSize="24" color={text.white} />}
        >
          <LogoutOption onClick={handleClickLogout}>ログアウト</LogoutOption>
        </Popover>
      </AppMenu>

      <Modal
        isOpen={isOpenLogoutConfirm}
        title="ログアウト"
        onClose={() => setIsOpenLogoutConfirm(false)}
        onOk={() => {
          setIsOpenLogoutConfirm(false);
          navigate(AppRoutes.logout);
        }}
      >
        <Typography variant="body14" color={text.primary} py="16px" component="div">
          Irohanaのすべてのプロダクトからログアウトされますが、よろしいでしょうか？
        </Typography>
      </Modal>
    </BoxWrapper>
  );
};

const BoxWrapper = styled(Box)`
  background-color: ${background.main};
  height: 56px;
  padding: 0px ${spacing.sm};
  border-bottom: 1px solid ${divider.middle};
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: ${elevation.lv1};
  position: sticky;
  top: 0;
  z-index: ${zIndex.appBar};
`;

const AppMenu = styled(Box)`
  display: flex;
  align-items: center;
`;

const StyledLink = styled(Link)`
  &.centered {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;

const LogoutOption = styled(Box)`
  font-size: 14px;
  padding: 4px 28px;
  cursor: pointer;
  :hover {
    background: ${divider.low};
    color: ${text.primary};
  }
  :focus {
    background: ${divider.middle};
    color: ${text.primary};
  }
`;
